import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Fade,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import ActionButton from '../components/ActionButton';
import BrandLogo from '../components/BrandLogo';
import { useAuth } from '../hooks/useAuth';

interface FormData {
  username: string;
  password: string;
}

interface FormErrors {
  username?: string;
  password?: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoggingIn, user } = useAuth();

  const [formData, setFormData] = useState<FormData>({
    username: '',
    password: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string>('');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (user) {
      setIsVisible(false);
      const timer = setTimeout(() => {
        navigate('/dashboard');
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [user, navigate]);

  const validateForm = () => {
    const errors: FormErrors = {};
    if (!formData.username) {
      errors.username = 'Username is required';
    }
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setError('');
    try {
      await login(formData.username, formData.password);
    } catch {
      setError('Invalid credentials. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Fade in={isVisible} timeout={400}>
        <Box
          sx={{
            width: '100%',
            maxWidth: 400,
          }}
        >
          <Box textAlign="center" mb={5}>
            <BrandLogo variant="horizontal" height={125} width="auto" />
          </Box>
          <form onSubmit={handleSubmit}>
            {error && (
              <Box mb={2}>
                <Typography color="error" variant="body2" align="center">
                  {error}
                </Typography>
              </Box>
            )}
            <TextField
              label="Username"
              type="text"
              name="username"
              placeholder="Enter your username"
              value={formData.username}
              onChange={handleInputChange}
              error={!!formErrors.username}
              helperText={formErrors.username}
              fullWidth
              required
              margin="normal"
              autoComplete="username"
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleInputChange}
              error={!!formErrors.password}
              helperText={formErrors.password}
              fullWidth
              required
              margin="normal"
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <ActionButton
              type="submit"
              variant="primary"
              fullWidth
              size="lg"
              loading={isLoggingIn}
              sx={{ mt: 3, mb: 2 }}
            >
              Sign In
            </ActionButton>
          </form>
        </Box>
      </Fade>
    </Box>
  );
};

export default Login;
